import React, { useCallback, useMemo } from 'react';
import { Box, Typography, Paper, Tooltip } from '@mui/material';
import { styled } from '@mui/material/styles';

interface VisualAvailabilityCalendarProps {
    availability: {
        [day: string]: string[]; // day -> array of time ranges (e.g., ["09:00-12:00", "14:00-18:00"])
    };
    onChange: (newAvailability: { [day: string]: string[] }) => void;
}

const days = ['Lunes', 'Martes', 'Miercoles', 'Jueves', 'Viernes', 'Sabado', 'Domingo'];
const hours = Array.from({ length: 24 }, (_, i) => `${i.toString().padStart(2, '0')}:00`);

const Cell = styled(Box)<{ active?: boolean }>(({ theme, active }) => ({
    height: 30,
    border: `1px solid ${theme.palette.divider}`,
    backgroundColor: active ? theme.palette.primary.main : 'transparent',
    cursor: 'pointer',
    transition: 'all 0.2s',
    '&:hover': {
        backgroundColor: active ? theme.palette.primary.dark : theme.palette.action.hover,
    },
}));

// Memoized cell component for better performance
const TimeCell = React.memo<{ dayIndex: number; hour: string; isActive: boolean; onMouseDown: (dayIndex: number, hour: string) => void; onMouseEnter: (dayIndex: number, hour: string) => void; day: string }>(
    ({ dayIndex, hour, isActive, onMouseDown, onMouseEnter, day }) => (
        <Tooltip key={`${day}-${hour}`} title={`${day} ${hour} - ${isActive ? 'Disponible' : 'Cerrado'}`}>
            <Cell
                active={isActive}
                onMouseDown={() => onMouseDown(dayIndex, hour)}
                onMouseEnter={() => onMouseEnter(dayIndex, hour)}
            />
        </Tooltip>
    )
);

// Helper: Convert time ranges to set of hours
const rangesToHours = (ranges: string[]): Set<string> => {
    const hours = new Set<string>();
    ranges.forEach(range => {
        const [start, end] = range.split('-');
        const startHour = parseInt(start.split(':')[0]);
        const endHour = parseInt(end.split(':')[0]);
        for (let h = startHour; h < endHour; h++) {
            hours.add(`${h.toString().padStart(2, '0')}:00`);
        }
    });
    return hours;
};

// Helper: Convert set of hours to time ranges
const hoursToRanges = (hours: Set<string>): string[] => {
    const sortedHours = Array.from(hours).sort();
    if (sortedHours.length === 0) return [];

    const ranges: string[] = [];
    let rangeStart = sortedHours[0];
    let prevHour = parseInt(sortedHours[0].split(':')[0]);

    for (let i = 1; i < sortedHours.length; i++) {
        const currentHour = parseInt(sortedHours[i].split(':')[0]);
        if (currentHour !== prevHour + 1) {
            // End of range
            ranges.push(`${rangeStart}-${(prevHour + 1).toString().padStart(2, '0')}:00`);
            rangeStart = sortedHours[i];
        }
        prevHour = currentHour;
    }
    // Close last range
    ranges.push(`${rangeStart}-${(prevHour + 1).toString().padStart(2, '0')}:00`);
    return ranges;
};

const VisualAvailabilityCalendar: React.FC<VisualAvailabilityCalendarProps> = ({
    availability,
    onChange,
}) => {
    const [isDragging, setIsDragging] = React.useState(false);
    const [dragMode, setDragMode] = React.useState<'add' | 'remove' | null>(null);

    // Convert ranges to hours for display - optimized with useMemo
    const availabilityHours = useMemo(() => {
        const result: { [day: string]: Set<string> } = {};
        days.forEach(day => {
            const dayKey = day.toLowerCase();
            result[dayKey] = rangesToHours(availability[dayKey] || []);
        });
        return result;
    }, [availability]);

    React.useEffect(() => {
        const handleGlobalMouseUp = () => {
            setIsDragging(false);
            setDragMode(null);
        };

        window.addEventListener('mouseup', handleGlobalMouseUp);
        return () => {
            window.removeEventListener('mouseup', handleGlobalMouseUp);
        };
    }, []);

    const updateSlot = useCallback((dayIndex: number, hour: string, mode: 'add' | 'remove') => {
        const dayKey = days[dayIndex].toLowerCase();
        const currentHours = new Set(availabilityHours[dayKey] || []);

        if (mode === 'add') {
            currentHours.add(hour);
        } else {
            currentHours.delete(hour);
        }

        // Convert back to ranges
        const newRanges = hoursToRanges(currentHours);

        onChange({
            ...availability,
            [dayKey]: newRanges,
        });
    }, [availability, availabilityHours, onChange]);

    const handleMouseDown = useCallback((dayIndex: number, hour: string) => {
        const dayKey = days[dayIndex].toLowerCase();
        const currentHours = availabilityHours[dayKey] || new Set();
        const isActive = currentHours.has(hour);

        const newMode = isActive ? 'remove' : 'add';
        setIsDragging(true);
        setDragMode(newMode);
        updateSlot(dayIndex, hour, newMode);
    }, [availabilityHours, updateSlot]);

    const handleMouseEnter = useCallback((dayIndex: number, hour: string) => {
        if (isDragging && dragMode) {
            updateSlot(dayIndex, hour, dragMode);
        }
    }, [isDragging, dragMode, updateSlot]);

    return (
        <Paper elevation={0} sx={{ p: 2, overflowX: 'auto', userSelect: 'none' }}>
            <Box sx={{ minWidth: 600 }}>
                {/* Header Row */}
                <Box sx={{ display: 'grid', gridTemplateColumns: '60px repeat(7, 1fr)' }}>
                    <Box sx={{ p: 1 }}></Box>
                    {days.map(day => (
                        <Box key={day} sx={{ p: 1, textAlign: 'center', fontWeight: 'bold' }}>
                            <Typography variant="caption">{day}</Typography>
                        </Box>
                    ))}
                </Box>

                {/* Time Rows */}
                {hours.map(hour => (
                    <Box key={hour} sx={{ display: 'grid', gridTemplateColumns: '60px repeat(7, 1fr)' }}>
                        <Box sx={{ p: 0.5, textAlign: 'right', pr: 2 }}>
                            <Typography variant="caption" color="text.secondary">{hour}</Typography>
                        </Box>
                        {days.map((day, index) => {
                            const dayKey = day.toLowerCase();
                            const isActive = availabilityHours[dayKey]?.has(hour) || false;
                            return (
                                <TimeCell
                                    key={`${day}-${hour}`}
                                    dayIndex={index}
                                    hour={hour}
                                    isActive={isActive}
                                    onMouseDown={handleMouseDown}
                                    onMouseEnter={handleMouseEnter}
                                    day={day}
                                />
                            );
                        })}
                    </Box>
                ))}
            </Box>
        </Paper>
    );
};

export default VisualAvailabilityCalendar;
